import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { v4 as uuidv4 } from 'uuid';
import './style.less';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Property } from 'types/property';
import { Icon } from 'shared/components/Icon';
import { setOrgProspectProperties, updateOrgSettings } from 'shared/store/org-slice';
import { TypeIndicator } from 'shared/components/TypeIndicator';
import { useAppSelector } from 'shared/store';
import { DEFAULT_FIELD_META } from 'browser/components/Settings/Integrations/HubspotConfigurationPage/HubspotMapping/constants';
import { useDispatch } from 'react-redux';
import { getAllProperties } from 'shared/api';
import { ProspectPropertySelector } from 'browser/components/ProspectPropertiesModal/ProspectPropertySelector';
import {
  OrgProspectPropertyGroup,
  OrgProspectPropertyWithMeta,
  StoredOrgProspectProperty,
} from 'types/user';

export type ProspectPropertyListDataType = {
  id: string;
  isNew: boolean;
  isCustom: boolean | undefined;
  propertyKey: string | undefined;
  propertyGroup: OrgProspectPropertyGroup | undefined;
  propertyName: string | undefined;
  label: string | undefined;
  type: string | undefined;
};

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<Icon icon="drag" height={16} width={16} />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const ExistingProperty: React.FC<ProspectPropertyListDataType> = props => {
  if (!props.type || !props.propertyKey) {
    return null;
  }
  return (
    <div className="ProspectProperty--existing ProspectProperty__property">
      <div className="ProspectProperty__dragHandle">
        <DragHandle />
      </div>
      <div className="ProspectProperty__label">{props.propertyName}</div>
      <div className="ProspectProperty__type">
        <TypeIndicator type={props.type === 'text' ? 'string' : props.type} />
      </div>
    </div>
  );
};

interface NewPropertyProps {
  dataSourceId: string;
  onSelect: (newProperty: OrgProspectPropertyWithMeta, dataSourceId: string) => void;
  availableContactProperties: OrgProspectPropertyWithMeta[];
  availableCompanyProperties: OrgProspectPropertyWithMeta[];
  selectedProperty: OrgProspectPropertyWithMeta | undefined;
}

const NewProperty: React.FC<NewPropertyProps> = props => {
  return (
    <div className="ProspectProperty--new ProspectProperty__property">
      <div className="ProspectProperty__dragHandle">
        <DragHandle />
      </div>
      <div className="ProspectProperty__label">
        <ProspectPropertySelector
          contactProperties={props.availableContactProperties}
          companyProperties={props.availableCompanyProperties}
          selectedProperty={props.selectedProperty}
          onSelect={(property: OrgProspectPropertyWithMeta) => {
            props.onSelect(property, props.dataSourceId);
          }}
        />
      </div>
    </div>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr
        className="ProspectProperty"
        {...props}
        ref={setNodeRef}
        style={style}
        {...attributes}
      />
    </RowContext.Provider>
  );
};

interface ProspectPropertiesSettingsProps {
  isStandalone: boolean;
  onChange?: (prospectProperties: OrgProspectPropertyWithMeta[]) => void;
}

export const ProspectPropertiesSettings: React.FC<ProspectPropertiesSettingsProps> = props => {
  const [dataSource, setDataSource] = useState<ProspectPropertyListDataType[]>([]);

  async function handleSave() {
    const outboundOrgProspectProperties: StoredOrgProspectProperty[] = dataSource
      .filter(property => property.propertyGroup && property.propertyKey)
      .map(
        property =>
          ({
            propertyKey: property.propertyKey,
            propertyGroup: property.propertyGroup,
            label: property.label,
          } as StoredOrgProspectProperty),
      );

    dispatch(
      updateOrgSettings({
        outboundProspectProperties: outboundOrgProspectProperties,
      }),
    );
    dispatch(setOrgProspectProperties(outboundOrgProspectProperties));

    setDataSource([
      ...dataSource.map((dataSourceProperty: ProspectPropertyListDataType) => ({
        ...dataSourceProperty,
        isNew: false,
        id: `${dataSourceProperty.propertyGroup}-${dataSourceProperty.propertyKey}`,
      })),
    ]);
  }

  useEffect(() => {
    if (props.onChange) {
      props.onChange(
        dataSource
          .filter(dataPoint => dataPoint.propertyGroup && dataPoint.propertyKey)
          .map(dataPoint => dataPoint) as OrgProspectPropertyWithMeta[],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  const [customContactProperties, setCustomContactProperties] = useState<Property[]>([]);
  const [customCompanyProperties, setCustomCompanyProperties] = useState<Property[]>([]);

  // Default properties are the same across integrations, so just use Hubspot Metadata
  const defaultContactProperties = Object.keys(DEFAULT_FIELD_META['contact']).map(
    key => ({
      ...DEFAULT_FIELD_META['contact'][key],
      propertyGroup: 'contact',
      propertyKey: key,
      propertyName: DEFAULT_FIELD_META['contact'][key]['displayName'],
      isCustom: false,
    }),
  );

  const defaultCompanyProperties = Object.keys(DEFAULT_FIELD_META['company']).map(
    key => ({
      ...DEFAULT_FIELD_META['company'][key],
      propertyGroup: 'company',
      propertyKey: key,
      propertyName: DEFAULT_FIELD_META['company'][key]['displayName'],
      isCustom: false,
    }),
  );

  const allContactPropertiesExceptSelected = useMemo(() => {
    const customProperties = (customContactProperties || []).map(prop => ({
      propertyGroup: 'contact',
      propertyName: prop.displayName,
      label: prop.displayName,
      type: prop.type,
      propertyKey: prop.key,
      isCustom: true,
    }));

    return [...defaultContactProperties, ...customProperties].filter(
      prop =>
        !dataSource
          .filter(prop => prop.propertyGroup === 'contact')
          .map(prop => prop.propertyKey)
          .includes(prop.propertyKey),
    );
  }, [customContactProperties, dataSource, defaultContactProperties]);

  const allCompanyPropertiesExceptSelected = useMemo(() => {
    const customProperties = (customCompanyProperties || []).map(prop => ({
      propertyGroup: 'company',
      propertyName: prop.displayName,
      label: prop.displayName,
      type: prop.type,
      propertyKey: prop.key,
      isCustom: true,
    }));

    return [...defaultCompanyProperties, ...customProperties].filter(
      prop =>
        !dataSource
          .filter(prop => prop.propertyGroup === 'company')
          .map(prop => prop.propertyKey)
          .includes(prop.propertyKey),
    );
  }, [customCompanyProperties, dataSource, defaultCompanyProperties]);

  const fetchAllCustomProperties = useCallback(async () => {
    try {
      const res = (await getAllProperties()).data;

      setCustomContactProperties(res?.contact || []);
      setCustomCompanyProperties(res?.company || []);
    } catch (err) {
      console.error(err.message || 'Failed to fetch custom properties');
    }
  }, []);

  useEffect(() => {
    fetchAllCustomProperties();
  }, [fetchAllCustomProperties]);

  const columns: ColumnsType<ProspectPropertyListDataType> = [
    {
      title: 'Property',
      dataIndex: 'property',
      align: 'left',
      render: (_, row) =>
        row.isNew ? (
          <NewProperty
            dataSourceId={row.id}
            key={row.id}
            selectedProperty={
              row.propertyGroup && row.propertyKey
                ? (row as OrgProspectPropertyWithMeta)
                : undefined
            }
            availableContactProperties={allContactPropertiesExceptSelected}
            availableCompanyProperties={allCompanyPropertiesExceptSelected}
            onSelect={(
              newProperty: OrgProspectPropertyWithMeta,
              dataSourceId: string,
            ) => {
              const propertyMeta = getPropertyMetadataByGroupKey(
                newProperty.propertyGroup,
                newProperty.propertyKey,
              );

              setDataSource([
                ...dataSource.map((dataSourceProperty: ProspectPropertyListDataType) => {
                  return dataSourceProperty.id === dataSourceId
                    ? {
                        ...dataSourceProperty,
                        propertyGroup: newProperty.propertyGroup,
                        propertyKey: newProperty.propertyKey,
                        propertyName: propertyMeta?.displayName,
                        label: propertyMeta?.displayName,
                        type: newProperty.type,
                      }
                    : { ...dataSourceProperty };
                }),
              ]);
            }}
          />
        ) : (
          <ExistingProperty key={row.id} {...row} />
        ),
    },
    {
      title: 'Sidebar Label',
      dataIndex: 'label',
      render: (_, row) => {
        return (
          <div className="ProspectProperty__sideLabel">
            <Input
              value={row.label}
              onChange={e => {
                setDataSource([
                  ...dataSource.map(
                    (dataSourceProperty: ProspectPropertyListDataType) => {
                      return dataSourceProperty.id === row.id
                        ? {
                            ...dataSourceProperty,
                            label: e.target.value,
                          }
                        : { ...dataSourceProperty };
                    },
                  ),
                ]);
              }}
            />
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'actions',
      render: (_, row) => {
        return (
          <div className="ProspectProperty__actions">
            <Button
              className="ProspectProperty__actions--removeProperty"
              type="ghost"
              size="small"
              icon={<Icon icon="thrash" height={16} width={16} />}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setDataSource([...dataSource.filter(property => property.id !== row.id)]);
              }}
            />
          </div>
        );
      },
    },
  ];

  const orgProspectProperties = useAppSelector(state => state.org.orgProspectProperties);
  const orgProspectPropertiesMeta = useAppSelector(
    state => state.org.orgPropspectPropertiesMeta,
  );
  const dispatch = useDispatch();

  const getPropertyMetadataByGroupKey = (
    propertyGroup: OrgProspectPropertyGroup,
    propertyKey: string,
  ) =>
    orgProspectPropertiesMeta === null
      ? null
      : orgProspectPropertiesMeta[propertyGroup][propertyKey];

  const currentOrgProspectProperties = useMemo(() => {
    return orgProspectPropertiesMeta === null
      ? []
      : orgProspectProperties.map(orgProspectProperty => {
          const propertyMeta = getPropertyMetadataByGroupKey(
            orgProspectProperty.propertyGroup,
            orgProspectProperty.propertyKey,
          );

          return {
            propertyName: propertyMeta?.displayName,
            label: orgProspectProperty.label || propertyMeta?.displayName,
            type: propertyMeta?.type,
            propertyKey: orgProspectProperty.propertyKey,
            propertyGroup: orgProspectProperty.propertyGroup,
            id: `${orgProspectProperty.propertyGroup}-${orgProspectProperty.propertyKey}`,
            isNew: false,
            isCustom: propertyMeta?.isCustom,
          };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgProspectProperties, orgProspectPropertiesMeta]);

  useEffect(() => {
    if (dataSource.length === 0 && currentOrgProspectProperties !== null) {
      setDataSource(currentOrgProspectProperties);
    }
  }, [currentOrgProspectProperties, dataSource.length]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource(prevState => {
        const activeIndex = prevState.findIndex(record => record.id === active?.id);
        const overIndex = prevState.findIndex(record => record.id === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  return (
    <div className="ProspectPropertiesSettings">
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map(i => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="id"
            components={{ body: { row: Row } }}
            columns={columns}
            pagination={false}
            dataSource={dataSource}
            className="ProspectPropertiesSettings__table"
          />
          <div className="ProspectPropertiesSettings__addPropertyButton">
            <Button
              type="ghost"
              onClick={() =>
                setDataSource(prevState => {
                  return [
                    ...prevState,
                    {
                      propertyName: undefined,
                      propertyGroup: undefined,
                      propertyKey: undefined,
                      type: undefined,
                      label: undefined,
                      id: uuidv4(),
                      isNew: true,
                      isCustom: undefined,
                    } as ProspectPropertyListDataType,
                  ];
                })
              }
            >
              <Icon icon="plus" width={12} height={12} />
              Add property
            </Button>
          </div>
          {props.isStandalone && (
            <div className="ProspectPropertiesSettings__saveChangesButton">
              <Button
                type="primary"
                onClick={() => {
                  handleSave();
                }}
              >
                <Icon icon="plus" width={12} height={12} />
                Save changes
              </Button>
            </div>
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};
